import type { MouseEvent } from 'react'
import {
  IconBookUpload,
  IconCheck,
  IconChevronDown,
  IconEdit,
  IconPlayerPlay,
  IconRotateRectangle,
  IconShare,
  IconTrash,
  IconUsers,
} from '@tabler/icons-react'
import { Button, Menu, Tooltip } from 'components'
import { hasPermission, useFeatureToggle, useMantineTheme, useMediaQuery } from 'hooks'
import { useTranslation } from 'react-i18next'
import { NewProjectActionMenuProps } from './interfaces'

export const NewProjectActionMenu = ({
  project,
  handleEdit,
  handleRemove,
  handlePublish,
  handleApprove,
  handleReopen,
  handleFinish,
  onShareWithTeachers,
  onEditStudents,
}: NewProjectActionMenuProps) => {
  const { t } = useTranslation()
  const theme = useMantineTheme()
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`)
  const isArcMosaico = useFeatureToggle('arc-mosaico-project') as boolean

  const isTutor = hasPermission(['tutor'])
  const isTutorOrManager = hasPermission(['manager', 'tutor'])

  const isDraft = project.status === 'draft'
  const isApproved = project.status === 'approved'
  const isFinished = project.status === 'finished'

  const isApplication = project.kind === 'application'
  const isTemplate = project.kind === 'template'

  // Use backend actions for permissions
  const canEdit = project.actions?.edit?.canAction ?? false
  const canRemove = project.actions?.delete?.canAction ?? false
  const canApprove = project.actions?.approve?.canAction ?? false
  const canPublish = project.actions?.publish?.canAction ?? false
  const canReopen = project.actions?.reopen?.canAction ?? false

  // Arc Mosaico: Override canFinish to allow tutors and managers
  const canFinish = isArcMosaico
    ? (isTutorOrManager && isApplication && isApproved)
    : (project.actions?.finish?.canAction ?? false)

  // Hide menu for published templates in Arc Mosaico (library view)
  // Draft templates should still show the menu for edit/remove actions
  if (isArcMosaico && isTemplate && !isDraft) return null
  if (project === null || !isTutorOrManager || (isTutor && isFinished && !isArcMosaico)) return null

  return (
    <Menu shadow='md' width={200}>
      <Menu.Target>
        <Button fullWidth={isMobile} variant='outline' size='md' rightIcon={<IconChevronDown />}>
          {t('general_buttons_actions')}
        </Button>
      </Menu.Target>

      <Menu.Dropdown>
        {canEdit && !(isArcMosaico && isFinished) && (
          <Menu.Item component='button' onClick={() => handleEdit(project.id)} icon={<IconEdit />}>
            {t('general_buttons_edit')}
          </Menu.Item>
        )}
        {isArcMosaico && onShareWithTeachers && (
          <Menu.Item component='button' onClick={onShareWithTeachers} icon={<IconShare size={16} />}>
            {t('arc_projects.share_with_teachers')}
          </Menu.Item>
        )}
        {isArcMosaico && isApproved && onEditStudents && (
          <Menu.Item component='button' onClick={onEditStudents} icon={<IconUsers size={16} />}>
            {t('arc_projects.edit_assigned_students')}
          </Menu.Item>
        )}
        {!isArcMosaico && isTemplate && isDraft ? (
          canPublish ? (
            <Menu.Item component='button' onClick={() => handlePublish(project.id)} icon={<IconBookUpload />}>
              {t('general_buttons_publish')}
            </Menu.Item>
          ) : (
            <Tooltip label={t('general_publish_draft_info')} withinPortal withArrow position='top'>
              <Menu.Item
                component='button'
                icon={<IconBookUpload />}
                onClick={(event: MouseEvent<HTMLButtonElement>) => event.preventDefault()}
                data-disabled
                disabled={true}
                sx={{ '&[data-disabled]': { pointerEvents: 'all' } }}
              >
                {t('general_buttons_publish')}
              </Menu.Item>
            </Tooltip>
          )
        ) : null}
        {!isArcMosaico && isApplication && isDraft ? (
          canApprove ? (
            <Menu.Item component='button' onClick={() => handleApprove(project.id)} icon={<IconPlayerPlay />}>
              {t('general_buttons_start')}
            </Menu.Item>
          ) : (
            <Tooltip label={t('general_start_project_info')} withinPortal withArrow position='top'>
              <Menu.Item
                component='button'
                icon={<IconPlayerPlay />}
                onClick={(event: MouseEvent<HTMLButtonElement>) => event.preventDefault()}
                data-disabled
                disabled={true}
                sx={{ '&[data-disabled]': { pointerEvents: 'all' } }}
              >
                {t('general_buttons_start')}
              </Menu.Item>
            </Tooltip>
          )
        ) : null}
        {canReopen && (
          <Menu.Item component='button' onClick={() => handleReopen(project.id)} icon={<IconRotateRectangle />}>
            {t('general_buttons_reopen')}
          </Menu.Item>
        )}
        {isApproved ? (
          canFinish ? (
            <Menu.Item component='button' onClick={() => handleFinish(project.id)} icon={<IconCheck />}>
              {t('general_buttons_finish')}
            </Menu.Item>
          ) : (
            <Tooltip label={t('general_finish_project_info')} withinPortal withArrow position='top'>
              <Menu.Item
                component='button'
                icon={<IconCheck />}
                onClick={(event: MouseEvent<HTMLButtonElement>) => event.preventDefault()}
                data-disabled
                disabled={true}
                sx={{ '&[data-disabled]': { pointerEvents: 'all' } }}
              >
                {t('general_buttons_finish')}
              </Menu.Item>
            </Tooltip>
          )
        ) : null}
        {canRemove && <Menu.Divider />}
        {canRemove && <Menu.Label>{t('general_careful')}</Menu.Label>}
        {canRemove && (
          <Menu.Item component='button' color='red' onClick={() => handleRemove(project.id)} icon={<IconTrash />}>
            {t('general_buttons_remove')}
          </Menu.Item>
        )}
      </Menu.Dropdown>
    </Menu>
  )
}
